import React, { useEffect, useRef, useImperativeHandle, useState } from "react";
import "mathlive";

let _keyboardConfigured = false;
let activeKeyboardField = null;
const mobileKeyboard = () => window.matchMedia?.("(pointer: coarse), (max-width: 768px)").matches;
function keepFieldVisible(field) {
  const scroller = field.closest(".ql-content");
  if (!scroller) return;
  const bounds = field.getBoundingClientRect();
  const area = scroller.getBoundingClientRect();
  const bottom = Math.min(area.bottom, window.mathVirtualKeyboard?.visible ? window.mathVirtualKeyboard.boundingRect.top : area.bottom);
  if (bounds.bottom > bottom - 12) scroller.scrollTop += bounds.bottom - bottom + 12;
  else if (bounds.top < area.top + 12) scroller.scrollTop -= area.top + 12 - bounds.top;
}

function configureKeyboard() {
  if (_keyboardConfigured || !window.mathVirtualKeyboard) return;
  _keyboardConfigured = true;
  if (mobileKeyboard()) {
    let host = document.getElementById("ql-math-keyboard-host");
    if (!host) {
      host = document.createElement("div");
      host.id = "ql-math-keyboard-host";
      document.body.appendChild(host);
    }
    const keyboard = window.mathVirtualKeyboard;
    keyboard.container = host; // Prevent MathLive from adding padding to the page body.
    const resize = () => {
      const viewportBottom = (window.visualViewport?.offsetTop || 0) + (window.visualViewport?.height || window.innerHeight);
      const safeBottom = Math.max(0, viewportBottom - host.getBoundingClientRect().bottom);
      const height = keyboard.visible ? Math.ceil(keyboard.boundingRect.height + safeBottom) + 6 : 0;
      document.documentElement.style.setProperty("--ql-math-keyboard-height", `${height}px`);
      document.documentElement.classList.toggle("ql-math-keyboard-open", keyboard.visible);
      requestAnimationFrame(() => {
        const field = activeKeyboardField;
        if (field?.isConnected) keepFieldVisible(field);
      });
    };
    keyboard.addEventListener("geometrychange", resize);
    keyboard.addEventListener("virtual-keyboard-toggle", resize);
  }
  window.mathVirtualKeyboard.layouts = ["numeric", "symbols", "alphabetic", "greek"];
}

// Boutons barre de raccourcis
const TOOLBAR_BTNS = [
  { l: "x²",  t: "^{}" },
  { l: "xₙ",  t: "_{}" },
  { l: "x/y", t: "\\dfrac{}{}" },
  { l: "√x",  t: "\\sqrt{}" },
  { l: "(·)", t: "\\left(\\right)" },
  null, // séparateur
  { l: "α", t: "\\alpha " },
  { l: "β", t: "\\beta " },
  { l: "γ", t: "\\gamma " },
  { l: "θ", t: "\\theta " },
  { l: "π", t: "\\pi " },
  null,
  { l: "×", t: "\\times " },
  { l: "÷", t: "\\div " },
  { l: "±", t: "\\pm " },
  { l: "≤", t: "\\leq " },
  { l: "≥", t: "\\geq " },
  { l: "≠", t: "\\neq " },
  { l: "°", t: "^\\circ " },
];

function MathToolbar({ onInsert }) {
  return (
    <div className="ql-math-toolbar">
      {TOOLBAR_BTNS.map((btn, i) =>
        btn === null ? (
          <span key={i} className="ql-math-tb-sep" />
        ) : (
          <button
            key={i}
            type="button"
            title={btn.t}
            className="ql-math-tb-btn"
            onMouseDown={(e) => { e.preventDefault(); onInsert(btn.t); }}
          >
            {btn.l}
          </button>
        )
      )}
    </div>
  );
}

/**
 * Éditeur mathématique inline (MathLive).
 * Props:
 *   mode        : "math" (défaut) | "text"
 *   multiline   : bool – champ plus grand
 *   showToolbar : bool – affiche la barre de raccourcis LaTeX (défaut: true)
 *   className   : classes CSS supplémentaires
 */
const MathEditor = React.forwardRef(function MathEditor(
  {
    value,
    onChange,
    onFocus,
    onBlur,
    placeholder,
    multiline,
    mode = "math",
    className = "",
    showToolbar = true,
    readOnly = false,
  },
  ref
) {
  const mfRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [focused, setFocused] = useState(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useImperativeHandle(ref, () => ({
    insert(latex) { mfRef.current?.insert(latex); },
    focus()       { mfRef.current?.focus(); },
    blur()        { mfRef.current?.blur(); },
    getValue()    { return mfRef.current?.getValue("latex") || ""; },
    setValue(v)   { mfRef.current?.setValue(v || "", { silently: true }); },
  }));

  useEffect(() => {
    const mf = mfRef.current;
    if (!mf) return;

    if (readOnly) {
      mf.setAttribute("read-only", "");
      mf.mathVirtualKeyboardPolicy = "manual";
      return;
    }

    mf.removeAttribute("read-only");
    mf.mathVirtualKeyboardPolicy = "onfocus";
    if (mobileKeyboard()) mf.onScrollIntoView = () => keepFieldVisible(mf);
    mf.smartFence = true;
    mf.smartSuperscript = true;
    if (mode === "text") mf.defaultMode = "text";

    window.addEventListener("math-virtual-keyboard-open", configureKeyboard, { once: true });
    configureKeyboard();

    const showKbd = () => {
      activeKeyboardField = mf;
      mf.focus({ preventScroll: true });
      try { window.mathVirtualKeyboard?.show(); } catch (_) {}
    };

    const handleInput  = () => onChangeRef.current?.(mf.getValue("latex"));
    const handleFocus  = () => { activeKeyboardField = mf; setFocused(true);  onFocus?.(); configureKeyboard(); try { window.mathVirtualKeyboard?.show(); } catch(_){} };
    const handleBlur   = () => { setFocused(false); onBlur?.(); };

    mf.addEventListener("touchstart", showKbd, { passive: true });

    mf.addEventListener("input",      handleInput);
    mf.addEventListener("focus",      handleFocus);
    mf.addEventListener("blur",       handleBlur);
    return () => {
      window.removeEventListener("math-virtual-keyboard-open", configureKeyboard);
      if (activeKeyboardField === mf) {
        window.mathVirtualKeyboard?.hide();
        activeKeyboardField = null;
      }
      mf.removeEventListener("input",      handleInput);
      mf.removeEventListener("focus",      handleFocus);
      mf.removeEventListener("blur",       handleBlur);
      mf.removeEventListener("touchstart", showKbd);
    };
  }, [onFocus, onBlur, mode, readOnly]);

  useEffect(() => {
    const mf = mfRef.current;
    if (!mf) return;
    if (mf.getValue("latex") !== (value || ""))
      mf.setValue(value || "", { silently: true });
  }, [value]);

  const handleToolbarInsert = (latex) => {
    mfRef.current?.insert(latex);
    mfRef.current?.focus();
  };

  const cls = ["ql-mathfield", multiline ? "ql-mathfield-multi" : "", className]
    .filter(Boolean).join(" ");

  return (
    <div className="ql-matheditor-wrap">
      {showToolbar && !readOnly && (
        <MathToolbar onInsert={handleToolbarInsert} />
      )}
      <math-field ref={mfRef} class={cls} placeholder={placeholder || ""} />
    </div>
  );
});

export default MathEditor;
